/**
 * Code-defined TanStack Router tree for clickt dashboard.
 *
 * We keep code-based routes (no codegen) so `tsc --noEmit` works on a fresh
 * checkout.
 *
 * Heavy routes (anything that touches Recharts / large data tables) are loaded
 * via `lazyRouteComponent` so the initial bundle stays under the 250KB budget.
 *
 * `/app/__internal/events` is wired only when `import.meta.env.DEV` is true; the
 * route literally doesn't exist in the production bundle (Rollup tree-shakes it).
 */

import { Skeleton, Spinner } from '@clickt/brand';
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { ApiError, api } from './api-client.ts';
import { ForgotPasswordPage } from './routes/forgot-password.tsx';
import { LoginPage } from './routes/login.tsx';
import { NotFoundPage } from './routes/not-found.tsx';
import { ResetPasswordPage } from './routes/reset-password.tsx';
import { SignupPage } from './routes/signup.tsx';
import { VerifyEmailPage } from './routes/verify-email.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    try {
      await api.me();
      throw redirect({ to: '/app' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
});

// Auth routes are tiny and shipped in the initial bundle so the unauthed-redirect
// path doesn't request a second chunk just to render a login form.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: ResetPasswordPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: VerifyEmailPage,
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData({
        queryKey: ['me'],
        queryFn: () => api.me(),
        staleTime: 60_000,
      });
      return { me };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: () => <Outlet />,
});

const pendingComponent = () => (
  <div className="min-h-[60vh] grid place-items-center text-cool-gray">
    <div className="flex items-center gap-3">
      <Spinner />
      <Skeleton className="h-4 w-32" />
    </div>
  </div>
);

// All authed routes load lazily so Recharts / heavy tables don't land in the
// initial chunk.
const appHomeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app',
  // Plan-driven landing per the dashboard-redesign brief:
  //   Agency (any channel count): /app/channels (the grid is their landing)
  //   Solo (free/pro):            /app/channels/$onlyId (auto-scope to their one channel)
  //   Zero channels (either):     fall through to today's home.tsx onboarding flow
  // home.tsx itself becomes a redirect-only stub once R4 ships the new Overview.
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData({
        queryKey: ['me'],
        queryFn: () => api.me(),
        staleTime: 60_000,
      });
      const primary = me.memberships[0];
      if (!primary) return;
      const channels = await context.queryClient.ensureQueryData({
        queryKey: ['channels', primary.workspace_id],
        queryFn: () => api.listChannels(primary.workspace_id),
        staleTime: 60_000,
      });
      if (primary.plan === 'agency') {
        if (channels.channels.length > 0) {
          throw redirect({ to: '/app/channels' });
        }
        return; // brand-new agency with zero channels: fall through to onboarding
      }
      const only = channels.channels[0];
      if (only) {
        throw redirect({
          to: '/app/channels/$channelId',
          params: { channelId: only.id },
        });
      }
      // Solo with zero channels: fall through to today's pre-OAuth onboarding.
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: lazyRouteComponent(() => import('./routes/home.tsx'), 'HomePage'),
  pendingComponent,
});

const channelsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels',
  // Channels grid is agency-only per the dashboard-redesign brief. Solo creators
  // (free/pro) own exactly one channel; landing them on a grid with a single
  // card is busywork. If they hit /app/channels (typed URL, stale tab, deep
  // link from a teammate), bounce them straight into their channel root.
  //
  // Agency plans always render the grid, even with a single channel — R5 will
  // add an "+ Add another channel" card alongside the existing cards.
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData({
        queryKey: ['me'],
        queryFn: () => api.me(),
        staleTime: 60_000,
      });
      const primary = me.memberships[0];
      if (!primary || primary.plan === 'agency') return;
      const channels = await context.queryClient.ensureQueryData({
        queryKey: ['channels', primary.workspace_id],
        queryFn: () => api.listChannels(primary.workspace_id),
        staleTime: 60_000,
      });
      const only = channels.channels[0];
      if (only) {
        throw redirect({
          to: '/app/channels/$channelId',
          params: { channelId: only.id },
        });
      }
      // Solo user with zero channels — the pre-OAuth empty state lives on /app
      // (today's home.tsx onboarding flow). R4 will reshape this; for now, send
      // them back to the onboarding surface so they connect a channel.
      throw redirect({ to: '/app' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: lazyRouteComponent(() => import('./routes/channels-list.tsx'), 'ChannelsListPage'),
  pendingComponent,
});

// Channel root is the Overview surface (R4). The card-based Videos tab lives
// at the /videos subroute (R6).
const channelDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId',
  component: lazyRouteComponent(
    () => import('./routes/channel-overview.tsx'),
    'ChannelOverviewPage',
  ),
  pendingComponent,
});

// Channel-scoped Videos surface: card-based, state-aware, with the onboarding
// fork modal that fires once per channel after first OAuth import. Replaces
// the table-based channel-detail.tsx (deleted in R6).
const channelVideosRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId/videos',
  component: lazyRouteComponent(() => import('./routes/channel-videos.tsx'), 'ChannelVideosPage'),
  pendingComponent,
});

// T-200 "Beyond videos": the channel's off-video links (newsletter, emails,
// social bios, the channel page). The neutral /links path survives a rename.
const channelLinksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId/links',
  component: lazyRouteComponent(() => import('./routes/channel-links.tsx'), 'ChannelLinksPage'),
  pendingComponent,
});

const channelSalesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId/sales',
  component: lazyRouteComponent(() => import('./routes/channel-sales.tsx'), 'ChannelSalesPage'),
  pendingComponent,
});

// R12: per-sales-site detail page. Hosts the breakdown of which tracking
// links feed this site, by video, plus a performance chart and the same
// install actions surfaced on the channel-sales list card.
const salesSiteDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId/sales/$siteId',
  component: lazyRouteComponent(
    () => import('./routes/sales-site-detail.tsx'),
    'SalesSiteDetailPage',
  ),
  pendingComponent,
});

const channelSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/channels/$channelId/settings',
  component: lazyRouteComponent(
    () => import('./routes/channel-settings.tsx'),
    'ChannelSettingsPage',
  ),
  pendingComponent,
});

const videoDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/videos/$videoId',
  component: lazyRouteComponent(() => import('./routes/video-detail.tsx'), 'VideoDetailPage'),
  pendingComponent,
});

// /app/install is the pre-R8 workspace-level installer. R8 replaced it with
// per-channel Sales pages at /app/channels/$id/sales. Keep the URL alive as a
// permanent redirect so any saved bookmark, in-product link, or old marketing
// CTA still lands on a working surface. Fans out to the user's first channel's
// Sales page; if they have zero channels we send them through the channels
// list (which itself redirects through to onboarding for empty workspaces).
const installRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/install',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData({
        queryKey: ['me'],
        queryFn: () => api.me(),
        staleTime: 60_000,
      });
      const primary = me.memberships[0];
      if (!primary) {
        throw redirect({ to: '/app' });
      }
      const channels = await context.queryClient.ensureQueryData({
        queryKey: ['channels', primary.workspace_id],
        queryFn: () => api.listChannels(primary.workspace_id),
        staleTime: 60_000,
      });
      const first = channels.channels[0];
      if (first) {
        throw redirect({
          to: '/app/channels/$channelId/sales',
          params: { channelId: first.id },
        });
      }
      throw redirect({ to: '/app/channels' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: pendingComponent,
});

// Index: the history hub for returning users, or the steps for a first-timer.
const bulkRewriteRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/bulk-rewrite',
  // Optional `?channel=<id>` pre-selects the channel when launched from a
  // channel's Videos tab. On the hub it's forwarded to "New bulk rewrite".
  validateSearch: (s: Record<string, unknown>): { channel?: string } => ({
    channel: typeof s.channel === 'string' ? s.channel : undefined,
  }),
  component: lazyRouteComponent(() => import('./routes/bulk-rewrite.tsx'), 'BulkRewritePage'),
  pendingComponent,
});

// The steps flow on a stable URL (static `new` wins over the `$batchId` param).
const bulkRewriteNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/bulk-rewrite/new',
  validateSearch: (s: Record<string, unknown>): { channel?: string } => ({
    channel: typeof s.channel === 'string' ? s.channel : undefined,
  }),
  component: lazyRouteComponent(() => import('./routes/bulk-rewrite.tsx'), 'BulkRewriteNewPage'),
  pendingComponent,
});

// Batch status + records + rollback, addressable so it survives navigation.
const bulkRewriteBatchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/bulk-rewrite/$batchId',
  component: lazyRouteComponent(() => import('./routes/bulk-rewrite.tsx'), 'BulkRewriteBatchPage'),
  pendingComponent,
});

// `/app/settings/workspace` is retired (T-012): one account = one workspace, so
// there's no workspace identity surface to manage, and the niche
// attribution-window / consent / thank-you-pattern / click-filter controls that
// lived here are gone (server defaults stand). Keep the URL alive as a permanent
// redirect to Billing so saved bookmarks and stale in-product links still land
// on a working surface.
const settingsWorkspaceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/settings/workspace',
  beforeLoad: () => {
    throw redirect({ to: '/app/settings/billing' });
  },
  component: pendingComponent,
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/settings/billing',
  component: lazyRouteComponent(
    () => import('./routes/settings-billing.tsx'),
    'SettingsBillingPage',
  ),
  pendingComponent,
});

const settingsTeamRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/settings/team',
  component: lazyRouteComponent(() => import('./routes/settings-team.tsx'), 'SettingsTeamPage'),
  pendingComponent,
});

// T-170 founder admin. These routes DO ship in production (unlike the
// dev-only __internal route below): the API is the gate — anyone not on the
// ADMIN_USER_IDS allowlist gets 404 from /v1/app/admin/*. The redirect here
// only spares non-admins an empty error page. Lazy so the admin chunk stays
// out of the initial bundle for everyone.
const adminPeopleRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/app/admin',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData({
        queryKey: ['me'],
        queryFn: () => api.me(),
        staleTime: 60_000,
      });
      if (!me.user.is_platform_admin) throw redirect({ to: '/app' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: lazyRouteComponent(() => import('./routes/admin-people.tsx'), 'AdminPeoplePage'),
  pendingComponent,
});

// DEV-only debug route. Never builds into production bundles (`import.meta.env.DEV`
// is a build-time constant that becomes `false` in prod → Rollup tree-shakes the
// whole import below.).
const devRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => appLayoutRoute,
        path: '/app/__internal/events',
        component: lazyRouteComponent(
          () => import('./routes/internal-events.tsx'),
          'InternalEventsPage',
        ),
        pendingComponent,
      }),
    ]
  : [];

const appChildren = [
  appHomeRoute,
  channelsListRoute,
  channelDetailRoute,
  channelVideosRoute,
  channelLinksRoute,
  channelSalesRoute,
  salesSiteDetailRoute,
  channelSettingsRoute,
  videoDetailRoute,
  installRoute,
  bulkRewriteRoute,
  bulkRewriteNewRoute,
  bulkRewriteBatchRoute,
  settingsWorkspaceRoute,
  settingsBillingRoute,
  settingsTeamRoute,
  adminPeopleRoute,
  ...devRoutes,
];

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  appLayoutRoute.addChildren(appChildren),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFoundPage,
    defaultPendingComponent: pendingComponent,
  });
}

export type AppRouter = ReturnType<typeof buildRouter>;
